import { produce } from 'immer';

import {
  AtticSettings,
  BasicProfile,
  ComponentSide,
  GeneratableBearing,
  HorizontalGeneratableBearing,
  JackSettings,
  PiggybackSettings,
  Profile,
  ProfileJoint,
  ProfileLine,
  ProfileTrussSettings,
  VerticalGeneratableBearing,
  WebJoint,
  WebJointSegment,
} from '@paragon/contract-library';
import { distanceEquals } from '@paragon/geometry-library';
import { enqueueError } from '@paragon/notification-tools';
import { assertExhaustiveSwitch, updateIfChanged } from '@paragon/utilities';

import { ProfileClass } from '../../library/profiles/ProfileClass';
import {
  addNextLineToProfileFromUserInput,
  adjustHeelsToDesiredHeelHeight,
  applyProfileChange,
  calculateProfileLineGeometry,
  deleteFixedJoint,
  getLinesToCloseProfile,
  getNextJointLocation,
  getProfileIsClosed,
  isFullProfile,
  mutablyReplaceProfileJoint,
  mutablyUpdateProfileJointLocation,
  shiftWebJoint,
  updateFeatureAndFloatingJointsOnProfile,
  updateWebJointSegmentPanels,
} from '../../library/profiles/profileManipulation';
import { BearingInsertion } from '../selectors/bearingInsertionSelectors';
import { getActiveComponentDesign } from '../selectors/getActiveComponentDesignSelector';
import { getSingleSelectedItemEntryOrNull } from '../selectors/getSelectedItemSelector';
import {
  getInsertingOrRewebbingProfile,
  getInsertingProfile,
  getInsertingProfileClass,
  getInsertingProfileTrussSettings,
  getTopChordWidthDuringProfileInsertion,
} from '../selectors/profileSelectors';
import {
  clearInsertBearingComponentInsertion,
  ComponentInsertionStage,
  createWebSegmentComponentInsertion,
  createWebSegmentRewebbing,
  deleteWebSegmentsComponentInsertion,
  deleteWebSegmentsRewebbing,
  RewebbingProfileSettings,
  setInsertionProfileTrussSettings,
  setRewebbingProfileSettings,
} from '../slices/applicationSlice';
import { clearSelection } from '../slices/selectionsSlice';
import { rewebTruss } from './editComponentActions';
import { setComponentInsertionStage, updateInsertingTrussFromSettings } from './insertComponentDesignActions';
import { unloadInsertComponentDesign } from './loadActions';
import { selectItem } from './selectActions';
import { AsyncThunkActionVoid, ThunkActionVoid } from './thunkActions';

/**
 * All updates to profile truss settings should go through this method to make sure that "calculated properties" are
 * up-to-date with the new profile.
 */
export function cleanProfileTrussSettings(profileTrussSettings: ProfileTrussSettings) {
  const updatedProfile = updateFeatureAndFloatingJointsOnProfile(profileTrussSettings.profile);
  return { ...profileTrussSettings, profile: updatedProfile };
}

/**
 * Almost all updates to the insertion profile truss settings should go through this method so that the component can be
 * regenerated or cleared as appropriate. If you don't call this method, make sure you pass the profile truss settings
 * through `cleanProfileTrussSettings` and call `updateInsertingTrussFromSettings`.
 */
export const updateInsertingProfileTrussSettings =
  (profileTrussSettings: ProfileTrussSettings): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    // Check if the profile is valid before `cleanProfileTrussSettings`, since that might throw an error if there is a
    // profile line with equal end joint locations.
    const profileClassResult = ProfileClass.create(profileTrussSettings);
    if (profileClassResult.type === 'Err') {
      dispatch(enqueueError(`Failed to update profile. ${profileClassResult.err}`));
      return;
    }

    profileTrussSettings = cleanProfileTrussSettings(profileTrussSettings);

    dispatch(setInsertionProfileTrussSettings(profileTrussSettings));

    const state = getState();
    if (state.editor.editInProgress) return;

    const componentDesign = getActiveComponentDesign(state);
    let componentInsertionStage: ComponentInsertionStage | null = null;

    const { insertingObjectInfo } = state.application;
    if (insertingObjectInfo?.type === 'componentDesign') {
      componentInsertionStage = insertingObjectInfo.stage;
    }

    // The profile settings have been updated; now we deal with the consequences. There are several possible scenarios:
    if (getProfileIsClosed(profileTrussSettings.profile)) {
      if (componentInsertionStage === 'ChoosingShape') {
        // During stage one of truss insertion, once the user has fully drawn a profile we can be confident that they
        //   intend to use the freeform method, so move on to step two of insertion. The inserting truss will be updated
        //   if appropriate by `setComponentInsertionStage`.
        await dispatch(setComponentInsertionStage('ShapeDetails'));
      } else {
        // If it's not stage one of insertion, the user is likely just editing an existing profile. Update the insertion
        //   component accordingly.
        await dispatch(updateInsertingTrussFromSettings({ preview: true }));
      }
    } else if (componentDesign) {
      // If the profile is not yet closed then no valid component design can be generated from it. If there is an active
      //   component design it likely won't match the current profile, so clear it to avoid confusion.
      dispatch(clearSelection());
      dispatch(unloadInsertComponentDesign(componentDesign));
    }
  };

export const updateInsertingProfile =
  (profile: Profile): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const insertingProfileTrussSettings = getInsertingProfileTrussSettings(getState());
    await dispatch(updateInsertingProfileTrussSettings({ ...insertingProfileTrussSettings, profile }));
  };

export const updateActiveJackSettings =
  (jackSettings: JackSettings | null): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const profile = getInsertingProfile(getState());
    await dispatch(updateInsertingProfile({ ...profile, jackSettings }));
  };

export const updateActivePiggybackSettings =
  (piggybackSettings: PiggybackSettings | null): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const profile = getInsertingProfile(getState());
    const continuouslyBearing = profile.continuouslyBearing || !!piggybackSettings;
    await dispatch(updateInsertingProfile({ ...profile, piggybackSettings, continuouslyBearing }));
  };

export const updateActiveAtticSettings =
  (atticSettings: AtticSettings | null): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const profile = getInsertingProfile(getState());
    await dispatch(updateInsertingProfile({ ...profile, atticSettings }));
  };

export const shiftWebJointPosition =
  (webJointGuid: string, deltaX: number): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const profile = getInsertingOrRewebbingProfile(getState());

    const updatedProfile = shiftWebJoint(profile, webJointGuid, deltaX);

    if (getState().application.rewebbingInfo) await dispatch(updateRewebbingProfile(updatedProfile));
    else if (isFullProfile(updatedProfile)) await dispatch(updateProfileWithWebJointEdit(updatedProfile, webJointGuid));
    else throw new Error();
  };

export const clearProfile = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const profile = getInsertingProfile(getState());
  await dispatch(updateInsertingProfile({ ...profile, profileJoints: [], profileLines: [] }));
};

export const removeLineFromProfile = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const profileResult = getInsertingProfileClass(getState());
  if (profileResult.type === 'Err') {
    dispatch(
      enqueueError(`Cannot remove line from profile: ${profileResult.err} Try clearing the profile to fix the issue.`),
    );
    return;
  }
  const profile = profileResult.ok;

  const selectedEntry = getSingleSelectedItemEntryOrNull(getState());
  const selectedItemGuid = selectedEntry?.item.guid ?? null;

  if (profile.profileData.profileLines.length === 0) return;
  const profileLinesToRemove = profile.getLastProfileLines();
  const updatedProfileLines = profile.profileData.profileLines.filter(
    (profileLine) => !profileLinesToRemove.some((line) => profileLine.guid === line.guid),
  );
  const updatedProfileJoints = profile.profileData.profileJoints.filter((joint) =>
    updatedProfileLines.some((line) => line.baseJointGuid === joint.guid || line.endJointGuid === joint.guid),
  );
  const updatedProfile: Profile = {
    ...profile.profileData,
    profileLines: updatedProfileLines,
    profileJoints: updatedProfileJoints,
  };
  if (
    selectedItemGuid &&
    profileLinesToRemove.some(
      (line) =>
        line.guid === selectedItemGuid ||
        line.baseJoint.guid === selectedItemGuid ||
        line.endJoint.guid === selectedItemGuid ||
        line.lineData.fixedJoints.some((fixedJoint) => fixedJoint.guid === selectedItemGuid) ||
        line.lineData.webJointSegments.some((webJointSegment) =>
          webJointSegment.floatingJoints.some((floatingJoint) => floatingJoint.guid === selectedItemGuid),
        ),
    )
  ) {
    const componentDesign = getActiveComponentDesign(getState());
    if (componentDesign) dispatch(selectItem(componentDesign, 'componentDesign', { force: true }));
  }
  await dispatch(updateInsertingProfile(updatedProfile));
};

export const addProfileLineToProfile =
  (distance: number, slope: number, profileClass: ProfileClass): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const topChordWidth = getTopChordWidthDuringProfileInsertion(getState());
    const updatedProfile = addNextLineToProfileFromUserInput(
      { profile: profileClass, topChordWidth },
      { distance, slope },
    );
    if (updatedProfile) {
      await dispatch(updateInsertingProfile(updatedProfile));
    }
  };

export const addProfileLine =
  (distance: number, slope: number): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const profileClassResult = getInsertingProfileClass(getState());
    if (profileClassResult.type === 'Err') {
      dispatch(
        enqueueError(`Cannot add profile line: ${profileClassResult.err} Try clearing the profile to fix the issue.`),
      );
    } else {
      await dispatch(addProfileLineToProfile(distance, slope, profileClassResult.ok));
    }
  };

export const closeProfile = (): AsyncThunkActionVoid => async (dispatch, getState) => {
  const profileClassResult = getInsertingProfileClass(getState());
  if (profileClassResult.type === 'Err') {
    dispatch(
      enqueueError(`Cannot close profile: ${profileClassResult.err} Try clearing the profile to fix the issue.`),
    );
    return;
  }
  const profileClass = profileClassResult.ok;

  const topChordWidth = getTopChordWidthDuringProfileInsertion(getState());
  const profileClosingChange = getLinesToCloseProfile(profileClass, topChordWidth);
  const updatedProfile = applyProfileChange(profileClass.profileData, profileClosingChange);

  if (updatedProfile) {
    await dispatch(updateInsertingProfile(updatedProfile));
  }
};

// `leftOrBaseJointIsAnchored` means that the left joint is anchored if the web joint segment is non-vertical. If the
// web joint segment is vertical, then the base joint is anchored.
export const updateDistanceAndSlopeForProfileLine =
  (
    profileLine: ProfileLine,
    distance: number,
    slope: number,
    leftOrBaseJointIsAnchored: boolean,
  ): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const profileTrussSettings = getInsertingProfileTrussSettings(getState());
    const { profile, span } = profileTrussSettings;

    const profileLineGeometry = calculateProfileLineGeometry(profile, profileLine);
    // If the slope is being changed to or from being vertical, quit nicely.
    if (Math.abs(slope) >= 90 !== distanceEquals(profileLineGeometry.basePoint.x, profileLineGeometry.endPoint.x))
      return;

    const baseJointIsAnchored =
      profileLineGeometry.basePoint.x < profileLineGeometry.endPoint.x === leftOrBaseJointIsAnchored;
    const jointToMoveGuid = baseJointIsAnchored ? profileLine.endJointGuid : profileLine.baseJointGuid;
    const anchoredJointLocation = baseJointIsAnchored ? profileLineGeometry.basePoint : profileLineGeometry.endPoint;
    const signedDistance = leftOrBaseJointIsAnchored ? distance : -distance;
    const newLocation = getNextJointLocation(anchoredJointLocation, signedDistance, slope, span);

    const updatedProfile = produce(profile, (draftProfile) => {
      mutablyUpdateProfileJointLocation(draftProfile, jointToMoveGuid, newLocation);
    });

    await dispatch(updateInsertingProfile(updatedProfile));
  };

export const updateNumberOfPanelsOnWebJointSegment =
  (webJointSegment: WebJointSegment, numberOfPanels: number | null): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const profile = getInsertingOrRewebbingProfile(getState());

    const updatedProfile = updateWebJointSegmentPanels(webJointSegment.guid, numberOfPanels)(profile);

    if (isFullProfile(updatedProfile)) await dispatch(updateInsertingProfile(updatedProfile));
    else await dispatch(updateRewebbingProfile(updatedProfile));
  };

export const updateProfileJoint =
  (joint: ProfileJoint, x: number, y: number): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const updatedProfile = produce(getInsertingProfile(getState()), (draftProfile) => {
      mutablyReplaceProfileJoint(draftProfile, { ...joint, location: { x, y } });
    });

    await dispatch(updateInsertingProfile(updatedProfile));
  };

export const updateInsertingProfileHeelHeight =
  (profile: ProfileClass, newHeelHeight: number, side: ComponentSide): AsyncThunkActionVoid =>
  async (dispatch) => {
    const adjustedProfile = adjustHeelsToDesiredHeelHeight(profile, newHeelHeight, side);
    await dispatch(updateInsertingProfile(adjustedProfile));
  };

export const updateInsertionProfileBearing =
  (bearing: GeneratableBearing, index: number): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const profile = getInsertingProfile(getState());
    const newProfile = updateIfChanged(profile)('generatableBearings')(index).set(bearing);
    await dispatch(updateInsertingProfile(newProfile));
  };

export const deleteInsertionProfileBearing =
  (index: number): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const profile = getInsertingProfile(getState());
    const newProfile = updateIfChanged(profile)('generatableBearings').map((bearings) => {
      if (!bearings) throw new Error();
      return [...bearings.slice(0, index), ...bearings.slice(index + 1, bearings.length)];
    });
    await dispatch(updateInsertingProfile(newProfile));
  };

export const addInsertionProfileBearing =
  (insertion: BearingInsertion): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const newBearing =
      insertion.trussSide === 'Bottom'
        ? createHorizontalBearing(insertion)
        : insertion.trussSide === 'Left' || insertion.trussSide === 'Right'
          ? createVerticalBearing(insertion)
          : assertExhaustiveSwitch(insertion.trussSide);

    const profile = getInsertingProfile(getState());
    const newProfile: Profile = { ...profile, generatableBearings: [...profile.generatableBearings, newBearing] };

    dispatch(clearInsertBearingComponentInsertion());
    await dispatch(updateInsertingProfile(newProfile));
  };

function createHorizontalBearing(insertion: BearingInsertion): HorizontalGeneratableBearing {
  const { bearingDefinitionPoint, justification, trussSide } = insertion;

  if (trussSide !== 'Bottom') throw new Error('Expected bearing to be on the bottom of the truss.');

  const anchor =
    justification === 'Center'
      ? 'Center'
      : justification === 'Port'
        ? 'Left'
        : justification === 'Starboard'
          ? 'Right'
          : assertExhaustiveSwitch(justification);

  return {
    generatableBearingType: 'HorizontalGeneratableBearing',
    guid: crypto.randomUUID(),
    bearingType: 'DoublePlate',
    chordLocation: 'Bottom',
    offset: bearingDefinitionPoint.x,
    width: 3.5,
    anchor,
    isContinuous: false,
    offsetSide: 'Left',
    connectors: [],
  };
}

function createVerticalBearing(insertion: BearingInsertion): VerticalGeneratableBearing {
  const { trussSide, chordType } = insertion;

  if (trussSide !== 'Left' && trussSide !== 'Right')
    throw new Error('Expected bearing to be on the left or right of the truss.');

  return {
    generatableBearingType: 'VerticalGeneratableBearing',
    guid: crypto.randomUUID(),
    bearingType: 'ToeNail',
    chordLocation: chordType,
    offset: 0,
    width: 3.5,
    offsetSide: trussSide,
    connectors: [],
  };
}

export const reselectWebJoint =
  (webJointGuid: string): ThunkActionVoid =>
  (dispatch, getState) => {
    const profile = getInsertingOrRewebbingProfile(getState());
    const webJoint = profile.profileLines
      .flatMap((line) => line.fixedJoints.concat(line.webJointSegments.flatMap(({ floatingJoints }) => floatingJoints)))
      .find((webJoint) => webJoint.guid === webJointGuid);
    if (webJoint) dispatch(selectItem(webJoint, 'webJoint'));
  };

export const regenerateTrussAndReselectWebJoint =
  (webJointGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const profile = getInsertingOrRewebbingProfile(getState());
    const isRewebbing = !!getState().application.rewebbingInfo;

    if (isRewebbing) await dispatch(rewebTruss());
    else if (getProfileIsClosed(profile)) await dispatch(updateInsertingTrussFromSettings({ preview: true }));

    dispatch(reselectWebJoint(webJointGuid));
  };

export const updateProfileWithWebJointEdit =
  (updatedProfile: Profile, webJointGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const newProfile = updateFeatureAndFloatingJointsOnProfile(updatedProfile);
    const { insertingObjectInfo } = getState().application;
    if (insertingObjectInfo?.type !== 'componentDesign' || insertingObjectInfo.definitionType !== 'Profile') return;
    const { loadedInfo } = insertingObjectInfo;
    if (!loadedInfo.isLoaded) return;
    await dispatch(updateInsertingProfile(newProfile));
    dispatch(reselectWebJoint(webJointGuid));
  };

export const deleteFixedWebJoint =
  (joint: WebJoint): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const profile = getInsertingOrRewebbingProfile(getState());
    const newProfile = deleteFixedJoint(joint.guid)(profile);
    if (isFullProfile(newProfile)) await dispatch(updateProfileWithWebJointEdit(newProfile, joint.guid));
    else await dispatch(updateRewebbingProfile(newProfile));
  };

export const deleteWebSegmentsAndRewebTruss =
  (webSegmentGuids: readonly string[]): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { insertingObjectInfo, rewebbingInfo } = getState().application;
    if (insertingObjectInfo?.type === 'componentDesign') {
      dispatch(deleteWebSegmentsComponentInsertion({ webSegmentGuids: webSegmentGuids }));
      await dispatch(updateInsertingTrussFromSettings({ preview: true, useWebbingInfo: true }));
    } else if (rewebbingInfo) {
      dispatch(deleteWebSegmentsRewebbing({ webSegmentGuids: webSegmentGuids }));
      await dispatch(rewebTruss(true));
    } else {
      throw new Error('Expected to be inserting or rewebbing truss');
    }
  };

export const createWebSegmentAndRewebTruss =
  (webbingRegionIndex: number, baseJointGuid: string, endJointGuid: string): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { insertingObjectInfo, rewebbingInfo } = getState().application;
    if (insertingObjectInfo?.type === 'componentDesign') {
      dispatch(createWebSegmentComponentInsertion({ webbingRegionIndex, baseJointGuid, endJointGuid }));
      await dispatch(updateInsertingTrussFromSettings({ preview: true, useWebbingInfo: true }));
    } else if (rewebbingInfo) {
      dispatch(createWebSegmentRewebbing({ webbingRegionIndex, baseJointGuid, endJointGuid }));
      await dispatch(rewebTruss(true));
    } else {
      throw new Error('Expected to be inserting or rewebbing truss');
    }
  };

const updateRewebbingProfile =
  (updatedProfile: BasicProfile): AsyncThunkActionVoid =>
  async (dispatch, getState) => {
    const { rewebbingInfo } = getState().application;
    if (!rewebbingInfo) throw new Error('Expected to be rewebbing profile.');
    // no change => nothing to do
    if (updatedProfile === rewebbingInfo.profileSettings.profile) return;
    dispatch(
      setRewebbingProfileSettingsWithUpdatedJoints({ ...rewebbingInfo.profileSettings, profile: updatedProfile }),
    );
    // if no (rewebbing web joint) edit is in progress, go ahead and regenerate the truss and re-select the joint
    if (!getState().editor.editInProgress) {
      await dispatch(rewebTruss());
    }
  };

export const setRewebbingProfileSettingsWithUpdatedJoints =
  (profileSettings: RewebbingProfileSettings): ThunkActionVoid =>
  (dispatch) => {
    const profileWithJoints = updateFeatureAndFloatingJointsOnProfile(profileSettings.profile);
    const newProfileSettings: RewebbingProfileSettings = { ...profileSettings, profile: profileWithJoints };
    dispatch(setRewebbingProfileSettings(newProfileSettings));
  };
